/**
 * Client side of the quick search rules (server: `shared/src/search.ts`; the web app does not import server
 * code). Text is compared without case, Polish diacritics, spaces and punctuation.
 */

/** One character as the search sees it: lower case, no diacritics, "ł" as "l"; '' for spaces and punctuation. */
function searchChar(c: string): string {
  const n = c.normalize('NFD').replace(/\p{M}+/gu, '').toLowerCase().replace(/ł/g, 'l');
  return /^[\p{L}\p{N}]+$/u.test(n) ? n : '';
}

/** The query's words, compacted ("QRP-40" → "qrp40"), without empty ones. */
export function searchWords(query: string): string[] {
  return query
    .split(/\s+/)
    .map((w) => [...w].map(searchChar).join(''))
    .filter((w) => w !== '');
}

/**
 * Splits `text` into parts that match the query (shown in bold) and the rest. A word of the query matches a
 * run of letters and digits in the text ignoring what lies between them ("qrp 40" marks "QRP40", "qrp40"
 * marks "QRP-40"); the glued query is tried too.
 */
export function highlight(text: string, query: string): { text: string; match: boolean }[] {
  const chars = [...text];
  // compact form of the text and, for each of its characters, the index of the original character
  let compact = '';
  const origin: number[] = [];
  chars.forEach((c, i) => {
    for (const n of searchChar(c)) {
      compact += n;
      origin.push(i);
    }
  });
  const words = searchWords(query);
  const marked = new Array<boolean>(chars.length).fill(false);
  for (const w of [words.join(''), ...words]) {
    if (w === '') continue;
    const at = compact.indexOf(w);
    if (at === -1) continue;
    for (let k = at; k < at + w.length; k++) marked[origin[k]!] = true;
  }
  // characters between two marked ones (a space, a dash) belong to the match
  for (let i = 1; i < chars.length - 1; i++) {
    if (!marked[i] && searchChar(chars[i]!) === '' && marked[i - 1]) {
      let j = i;
      while (j < chars.length && searchChar(chars[j]!) === '') j++;
      if (j < chars.length && marked[j]) for (let k = i; k < j; k++) marked[k] = true;
    }
  }
  const parts: { text: string; match: boolean }[] = [];
  chars.forEach((c, i) => {
    const last = parts[parts.length - 1];
    if (last && last.match === marked[i]) last.text += c;
    else parts.push({ text: c, match: marked[i]! });
  });
  return parts;
}

/** Largest quantity typed in front of the query ("4 qrp40"). */
const MAX_PREFIX = 1_000_000;

/**
 * "4 qrp40", "4x qrp40", "4 x qrp40" → quantity 4 and the query "qrp40"; anything else → quantity 1 and the
 * whole text. A number alone ("40") is a query, not a quantity.
 */
export function parseQuantityPrefix(text: string): { quantity: number; query: string } {
  const m = /^\s*(\d{1,7})\s*(?:[x×*]\s+|\s+(?:[x×*]\s+)?)(\S.*)$/i.exec(text);
  if (m) {
    const quantity = Number(m[1]);
    if (quantity >= 1 && quantity <= MAX_PREFIX) return { quantity, query: m[2]!.trim() };
  }
  return { quantity: 1, query: text.trim() };
}
